import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Plug, Trash2, Pencil, HardDrive, Server, Cloud } from "lucide-react";
import RetentionFields from "@/components/RetentionFields";
import GoogleDriveFields from "@/components/GoogleDriveFields";

const GDriveIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M8.5 3h7l5.5 9.5h-7z" /><path d="M2 19l3.5-6.5 7 0L9 19z" />
    <path d="M22 12.5L18.5 19H9l3.5-6.5z" />
  </svg>
);

const EMPTY = {
  name: "", type: "s3", enabled: true,
  retention: { mode: "keep_last_n", keep_last_n: 7, keep_days: 30, gfs_hourly: 24, gfs_daily: 7, gfs_weekly: 4, gfs_monthly: 12 },
  host: "", port: 22, username: "", auth_method: "password", password: "", private_key: "", key_passphrase: "", base_path: "/backups",
  endpoint_url: "", region: "us-east-1", bucket: "", prefix: "backups", access_key: "", secret_key: "", path_style: true, verify_tls: true, object_lock: false,
  local_path: "",
  gdrive_auth_method: "oauth", gdrive_folder_id: "", gdrive_folder_name: "", gdrive_account_email: "", gdrive_service_account_json: "",
};

const TYPE_ICON = { sftp: Server, s3: Cloud, local: HardDrive, gdrive: GDriveIcon };

export default function Destinations() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState({});

  const load = useCallback(() => {
    api.get("/destinations").then((r) => setItems(r.data)).catch(() => toast.error(t("destinations.loadFailed")));
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const openCreate = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...EMPTY, ...d, password: "", private_key: "", secret_key: "", key_passphrase: "", gdrive_service_account_json: "" }); setEditId(d.id); setOpen(true); };

  const save = async () => {
    if (form.type === "gdrive" && !form.gdrive_folder_id) {
      toast.error(t("destinations.gdriveFolderRequired")); return;
    }
    if (form.type === "gdrive" && !editId && form.gdrive_auth_method === "service_account" && !form.gdrive_service_account_json) {
      toast.error(t("destinations.gdriveSaJsonFirst")); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, port: Number(form.port) };
      ["password", "private_key", "secret_key", "key_passphrase", "gdrive_service_account_json"].forEach((k) => { if (editId && !payload[k]) delete payload[k]; });
      if (editId) { await api.put(`/destinations/${editId}`, payload); toast.success(t("destinations.updated")); }
      else { await api.post("/destinations", payload); toast.success(t("destinations.created")); }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("destinations.saveFailed")); }
    finally { setSaving(false); }
  };

  const test = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await api.post(`/destinations/${id}/test`);
      if (r.data.ok) toast.success(t("destinations.verified"));
      else toast.error(r.data.error || t("destinations.testFailed"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("destinations.testFailed")); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const del = async (id) => {
    if (!window.confirm(t("destinations.confirmDelete"))) return;
    try { await api.delete(`/destinations/${id}`); toast.success(t("destinations.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("destinations.deleteFailed")); }
  };

  return (
    <div data-testid="destinations-page">
      <PageHeader title={t("destinations.title")} subtitle={t("destinations.subtitle")}
        actions={<Button data-testid="add-destination-button" onClick={openCreate} className="rounded-none uppercase font-semibold bg-[#0A0A0A] hover:bg-[#0052FF]"><Plus className="h-4 w-4 mr-1.5" /> {t("destinations.addDestination")}</Button>} />
      <div className="p-6 md:p-8">
        {items.length === 0 ? (
          <div className="grid-panel p-16 text-center">
            <p className="text-muted-foreground">{t("destinations.noDest")}</p>
            <Button onClick={openCreate} className="mt-4 rounded-none uppercase" data-testid="empty-add-destination">{t("destinations.addDestSmall")}</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {items.map((d) => {
              const Icon = TYPE_ICON[d.type] || HardDrive;
              return (
                <div key={d.id} className="grid-panel p-5" data-testid={`destination-card-${d.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 bg-secondary flex items-center justify-center"><Icon className="h-4 w-4" /></div>
                      <div>
                        <p className="font-semibold">{d.name}</p>
                        <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{d.type}</p>
                      </div>
                    </div>
                    <StatusBadge status={d.health_status || "untested"} />
                  </div>
                  <div className="mt-4 font-mono text-xs text-muted-foreground space-y-1">
                    {d.type === "sftp" && <p>{d.username}@{d.host}:{d.port}{d.base_path}</p>}
                    {d.type === "s3" && <p>{d.bucket}/{d.prefix} @ {d.endpoint_url || d.region}</p>}
                    {d.type === "local" && <p>{d.local_path}</p>}
                    {d.type === "gdrive" && <p className="truncate">{(d.gdrive_folder_name || d.gdrive_folder_id)}{d.gdrive_account_email ? ` · ${d.gdrive_account_email}` : ""}</p>}
                    <p>{t("destinations.retentionLabel", { mode: d.retention?.mode })}</p>
                    <p>{t("destinations.testedLabel", { date: fmtDate(d.last_tested) })}</p>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-none flex-1" disabled={busy[d.id]} onClick={() => test(d.id)} data-testid={`test-destination-${d.id}`}>
                      <Plug className="h-3.5 w-3.5 mr-1" /> {t("common.test")}
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-none" onClick={() => openEdit(d)} data-testid={`edit-destination-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => del(d.id)} data-testid={`delete-destination-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="destination-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? t("destinations.editTitle") : t("destinations.newTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <Field label={t("destinations.name")}><Input data-testid="dest-name" value={form.name} onChange={set("name")} className="rounded-none" /></Field>
            <Field label={t("destinations.type")}>
              <Select value={form.type} onValueChange={set("type")} disabled={!!editId}>
                <SelectTrigger className="rounded-none" data-testid="dest-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="s3">{t("destinations.typeS3")}</SelectItem>
                  <SelectItem value="sftp">{t("destinations.typeSftp")}</SelectItem>
                  <SelectItem value="local">{t("destinations.typeLocal")}</SelectItem>
                  <SelectItem value="gdrive">{t("destinations.typeGdrive")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {form.type === "sftp" && (<>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="col-span-2"><Field label={t("destinations.host")}><Input data-testid="dest-host" value={form.host} onChange={set("host")} className="rounded-none" /></Field></div>
                <Field label={t("destinations.port")}><Input type="number" value={form.port} onChange={set("port")} className="rounded-none" /></Field>
              </div>
              <Field label={t("destinations.username")}><Input value={form.username} onChange={set("username")} className="rounded-none" /></Field>
              <Field label={t("destinations.authMethod")}>
                <Select value={form.auth_method} onValueChange={set("auth_method")}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="password">{t("destinations.authPassword")}</SelectItem><SelectItem value="key">{t("destinations.authKey")}</SelectItem></SelectContent>
                </Select>
              </Field>
              {form.auth_method === "password"
                ? <Field label={t("destinations.password")}><Input type="password" value={form.password} onChange={set("password")} className="rounded-none" /></Field>
                : <>
                    <Field label={t("destinations.privateKey")}><textarea value={form.private_key} onChange={set("private_key")} rows={3} className="w-full rounded-none border border-input bg-card p-2 font-mono text-xs" /></Field>
                    <Field label={t("destinations.keyPassphrase")}><Input type="password" value={form.key_passphrase} onChange={set("key_passphrase")} className="rounded-none" /></Field>
                  </>}
              <Field label={t("destinations.basePath")}><Input value={form.base_path} onChange={set("base_path")} className="rounded-none" /></Field>
            </>)}

            {form.type === "s3" && (<>
              <Field label={t("destinations.endpoint")}><Input data-testid="dest-endpoint" value={form.endpoint_url} onChange={set("endpoint_url")} className="rounded-none" placeholder="https://minio.example.com" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t("destinations.region")}><Input value={form.region} onChange={set("region")} className="rounded-none" /></Field>
                <Field label={t("destinations.bucket")}><Input data-testid="dest-bucket" value={form.bucket} onChange={set("bucket")} className="rounded-none" /></Field>
              </div>
              <Field label={t("destinations.prefix")}><Input value={form.prefix} onChange={set("prefix")} className="rounded-none" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t("destinations.accessKey")}><Input value={form.access_key} onChange={set("access_key")} className="rounded-none" /></Field>
                <Field label={t("destinations.secretKey")}><Input type="password" value={form.secret_key} onChange={set("secret_key")} className="rounded-none" /></Field>
              </div>
              <div className="flex items-center justify-between"><Label>{t("destinations.pathStyle")}</Label><Switch checked={form.path_style} onCheckedChange={set("path_style")} /></div>
              <div className="flex items-center justify-between"><Label>{t("destinations.verifyTls")}</Label><Switch checked={form.verify_tls} onCheckedChange={set("verify_tls")} /></div>
              <div className="flex items-center justify-between"><Label>{t("destinations.objectLock")}</Label><Switch checked={form.object_lock} onCheckedChange={set("object_lock")} /></div>
            </>)}

            {form.type === "local" && (
              <Field label={t("destinations.localPath")}><Input data-testid="dest-localpath" value={form.local_path} onChange={set("local_path")} className="rounded-none" placeholder="/var/backups/mariadb" /></Field>
            )}

            {form.type === "gdrive" && (
              <GoogleDriveFields form={form} set={set} editId={editId} />
            )}

            <RetentionFields value={form.retention} onChange={(r) => setForm((f) => ({ ...f, retention: r }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button data-testid="save-destination-button" onClick={save} disabled={saving} className="rounded-none uppercase bg-[#0A0A0A] hover:bg-[#0052FF]">{saving ? t("common.saving") : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
